import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FaReceipt,
  FaCreditCard,
  FaMobile,
  FaMoneyBillWave,
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaCalendar,
  FaTag,
  FaStore,
  FaFileInvoice,
} from "react-icons/fa";
import { transactionsAPI } from "../services/api";
import toast from "react-hot-toast";

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: #2d5016;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  background: ${(props) => props.bgColor};
  color: white;
  font-size: 1.5rem;
`;

const StatValue = styled.div`
  color: #2d3436;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #636e72;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const PaymentsList = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
`;

const ListTitle = styled.h2`
  color: #2d3436;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;

      &:focus {
        outline: none;
        border-color: #2d5016;
      }
`;

const PaymentCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const PaymentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const PaymentInfo = styled.div`
  flex: 1;
`;

const PaymentId = styled.h3`
  color: #2d3436;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const PaymentDate = styled.p`
  color: #636e72;
  font-size: 0.9rem;
  margin: 0;
`;

const PaymentAmount = styled.div`
  text-align: right;
`;

const Amount = styled.div`
  color: #00b894;
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case "completed":
        return "linear-gradient(45deg, #00b894, #00a085)";
      case "pending":
        return "linear-gradient(45deg, #fdcb6e, #e17055)";
      case "failed":
        return "linear-gradient(45deg, #e84393, #d63031)";
      case "refunded":
        return "linear-gradient(45deg, #6c5ce7, #a29bfe)";
      default:
        return "linear-gradient(45deg, #74b9ff, #0984e3)";
    }
  }};
  color: white;
`;

const PaymentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.bgColor};
  color: white;
  font-size: 0.8rem;
`;

const DetailText = styled.div`
  color: #636e72;
  font-size: 0.9rem;
`;

const PaymentActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ActionButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: linear-gradient(135deg, #2d5016, #4a7c59);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(45, 80, 22, 0.3);
          }
        `;
      case "secondary":
        return `
          background: #f8f9fa;
          color: #636e72;
          border: 1px solid #dee2e6;
          &:hover {
            background: #e9ecef;
          }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2d5016;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #636e72;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  color: #2d3436;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.9rem;
`;

// Transaction Details Modal Styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2d3436;
  font-size: 1.5rem;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #636e72;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #2d3436;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #2d3436;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  color: #636e72;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailValue = styled.div`
  color: #2d3436;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
`;

const AmountHighlight = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #00b894;
  text-align: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  margin: 1rem 0;
`;

const StatusBadgeModal = styled.span`
  display: inline-block;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  ${(props) => {
    switch (props.status) {
      case "completed":
        return `
          background: #d4edda;
          color: #155724;
        `;
      case "pending":
        return `
          background: #fff3cd;
          color: #856404;
        `;
      case "failed":
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      default:
        return `
          background: #e9ecef;
          color: #495057;
        `;
    }
  }}
`;

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalAmount: 0,
    successfulPayments: 0,
    pendingPayments: 0,
  });

  useEffect(() => {
    fetchPayments();
  }, [filter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      // Use transactions API instead of payments API since transactions are created when meal plans are purchased
      const params = {
        limit: 100, // Get more transactions
        ...(filter !== "all" ? { status: filter } : {}),
        ...(filter !== "all" && filter === "completed" ? { type: "payment" } : {}),
      };
      const response = await transactionsAPI.getTransactions(params);

      if (response.data.success) {
        // Transactions are returned in response.data.data.transactions
        const transactionsData = response.data.data?.transactions || response.data.data || [];
        const transactionsArray = Array.isArray(transactionsData) ? transactionsData : [];
        // Filter to only show payment transactions for the payments page
        const paymentTransactions = transactionsArray.filter(
          (t) => t.transaction_type === "payment"
        );
        setPayments(paymentTransactions);
        calculateStats(paymentTransactions);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to load payment history");
      // Ensure payments is always an array even on error
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (paymentsData) => {
    // Ensure paymentsData is an array
    const paymentsArray = Array.isArray(paymentsData) ? paymentsData : [];
    const totalPayments = paymentsArray.length;
    const totalAmount = paymentsArray.reduce(
      (sum, payment) => sum + parseFloat(payment.amount || 0),
      0
    );
    // Transactions use 'status' field, not 'payment_status'
    const successfulPayments = paymentsArray.filter(
      (p) => p.status === "completed"
    ).length;
    const pendingPayments = paymentsArray.filter(
      (p) => p.status === "pending"
    ).length;

    setStats({
      totalPayments,
      totalAmount,
      successfulPayments,
      pendingPayments,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GY", {
      style: "currency",
      currency: "GYD",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case "card":
        return FaCreditCard;
      case "mmg":
        return FaMobile;
      case "cash":
      case "cash_on_delivery":
        return FaMoneyBillWave;
      default:
        return FaReceipt;
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method) {
      case "card":
        return "linear-gradient(135deg, #2d5016, #4a7c59)";
      case "mmg":
        return "linear-gradient(135deg, #2d5016, #4a7c59)";
      case "cash":
      case "cash_on_delivery":
        return "linear-gradient(135deg, #ffc107, #ff9800)";
      default:
        return "linear-gradient(135deg, #2d5016, #4a7c59)";
    }
  };

  const handleDownloadReceipt = (transaction) => {
    try {
      // Generate receipt HTML
      const receiptHTML = generateReceiptHTML(transaction);
      
      // Create a blob and download link
      const blob = new Blob([receiptHTML], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `receipt-${transaction.id}-${new Date(transaction.transaction_date || transaction.created_at).toISOString().split("T")[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Also open in new window for printing
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(receiptHTML);
        printWindow.document.close();
        // Wait for content to load, then show print dialog
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
      }
      
      toast.success("Receipt downloaded successfully!");
    } catch (error) {
      console.error("Error generating receipt:", error);
      toast.error("Failed to generate receipt");
    }
  };

  const generateReceiptHTML = (transaction) => {
    const transactionDate = formatFullDate(transaction.transaction_date || transaction.created_at);
    const receiptNumber = transaction.receipt_number || `REC-${transaction.id}`;
    const invoiceNumber = transaction.invoice_number || `INV-${transaction.id}`;
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Receipt - Transaction #${transaction.id}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background: #f5f5f5;
      padding: 20px;
    }
    .receipt-container {
      max-width: 800px;
      margin: 0 auto;
      background: white;
      padding: 40px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }
    .receipt-header {
      text-align: center;
      border-bottom: 3px solid #2d5016;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .receipt-header h1 {
      color: #2d5016;
      font-size: 2rem;
      margin-bottom: 10px;
    }
    .receipt-header p {
      color: #666;
      font-size: 0.9rem;
    }
    .receipt-info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 30px;
    }
    .info-section {
      background: #f8f9fa;
      padding: 15px;
      border-radius: 8px;
    }
    .info-section h3 {
      color: #2d5016;
      font-size: 0.9rem;
      text-transform: uppercase;
      margin-bottom: 10px;
      letter-spacing: 0.5px;
    }
    .info-section p {
      margin: 5px 0;
      font-size: 0.9rem;
    }
    .amount-section {
      text-align: center;
      background: linear-gradient(135deg, #2d5016 0%, #4a7c59 100%);
      color: white;
      padding: 30px;
      border-radius: 12px;
      margin: 30px 0;
    }
    .amount-section .label {
      font-size: 0.9rem;
      opacity: 0.9;
      margin-bottom: 10px;
    }
    .amount-section .amount {
      font-size: 2.5rem;
      font-weight: 700;
    }
    .details-table {
      width: 100%;
      border-collapse: collapse;
      margin: 30px 0;
    }
    .details-table th {
      background: #2d5016;
      color: white;
      padding: 12px;
      text-align: left;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.5px;
    }
    .details-table td {
      padding: 12px;
      border-bottom: 1px solid #e9ecef;
    }
    .details-table tr:last-child td {
      border-bottom: none;
    }
    .details-table .label {
      color: #666;
      font-weight: 500;
      width: 40%;
    }
    .details-table .value {
      color: #333;
      font-weight: 600;
    }
    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      ${transaction.status === "completed" 
        ? "background: #d4edda; color: #155724;"
        : transaction.status === "pending"
        ? "background: #fff3cd; color: #856404;"
        : "background: #f8d7da; color: #721c24;"
      }
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #e9ecef;
      text-align: center;
      color: #666;
      font-size: 0.85rem;
    }
    .footer p {
      margin: 5px 0;
    }
    @media print {
      body {
        background: white;
        padding: 0;
      }
      .receipt-container {
        box-shadow: none;
        padding: 20px;
      }
      @page {
        margin: 1cm;
      }
    }
  </style>
</head>
<body>
  <div class="receipt-container">
    <div class="receipt-header">
      <h1>PAYMENT RECEIPT</h1>
      <p>NourishGY - Meal Plan Purchase</p>
    </div>

    <div class="amount-section">
      <div class="label">Total Amount Paid</div>
      <div class="amount">${formatCurrency(transaction.amount)}</div>
    </div>

    <div class="receipt-info">
      <div class="info-section">
        <h3>Transaction Details</h3>
        <p><strong>Receipt Number:</strong> ${receiptNumber}</p>
        <p><strong>Transaction ID:</strong> #${transaction.id}</p>
        <p><strong>Invoice Number:</strong> ${invoiceNumber}</p>
        <p><strong>Date:</strong> ${transactionDate}</p>
        <p><strong>Status:</strong> <span class="status-badge">${transaction.status || transaction.payment_status || "N/A"}</span></p>
      </div>
      <div class="info-section">
        <h3>Payment Information</h3>
        <p><strong>Payment Method:</strong> ${transaction.payment_method === "cash" ? "Cash" : transaction.payment_method === "mmg" ? "MMG Mobile Money" : transaction.payment_method === "card" ? "Credit/Debit Card" : transaction.payment_method?.toUpperCase() || "N/A"}</p>
        <p><strong>Amount:</strong> ${formatCurrency(transaction.amount)}</p>
        ${transaction.net_amount && transaction.net_amount !== transaction.amount ? `<p><strong>Net Amount:</strong> ${formatCurrency(transaction.net_amount)}</p>` : ""}
        ${transaction.fees && parseFloat(transaction.fees) > 0 ? `<p><strong>Fees:</strong> ${formatCurrency(transaction.fees)}</p>` : ""}
        <p><strong>Currency:</strong> ${transaction.currency || "GYD"}</p>
      </div>
    </div>

    ${transaction.subscription_id || transaction.meal_plan_title || transaction.vendor_name ? `
    <table class="details-table">
      <thead>
        <tr>
          <th colspan="2">Meal Plan Information</th>
        </tr>
      </thead>
      <tbody>
        ${transaction.meal_plan_title ? `
        <tr>
          <td class="label">Meal Plan</td>
          <td class="value">${transaction.meal_plan_title}</td>
        </tr>
        ` : ""}
        ${transaction.vendor_name ? `
        <tr>
          <td class="label">Vendor</td>
          <td class="value">${transaction.vendor_name}</td>
        </tr>
        ` : ""}
        ${transaction.subscription_id ? `
        <tr>
          <td class="label">Subscription ID</td>
          <td class="value">#${transaction.subscription_id}</td>
        </tr>
        ` : ""}
      </tbody>
    </table>
    ` : ""}

    ${transaction.description ? `
    <div class="info-section" style="margin-top: 20px;">
      <h3>Description</h3>
      <p>${transaction.description}</p>
    </div>
    ` : ""}

    ${transaction.external_transaction_id || transaction.external_payment_id ? `
    <table class="details-table">
      <thead>
        <tr>
          <th colspan="2">Reference Numbers</th>
        </tr>
      </thead>
      <tbody>
        ${transaction.external_transaction_id ? `
        <tr>
          <td class="label">External Transaction ID</td>
          <td class="value">${transaction.external_transaction_id}</td>
        </tr>
        ` : ""}
        ${transaction.external_payment_id ? `
        <tr>
          <td class="label">External Payment ID</td>
          <td class="value">${transaction.external_payment_id}</td>
        </tr>
        ` : ""}
      </tbody>
    </table>
    ` : ""}

    <div class="footer">
      <p><strong>Thank you for your purchase!</strong></p>
      <p>This is an official receipt for your transaction.</p>
      <p>For any inquiries, please contact our support team.</p>
      <p style="margin-top: 15px; font-size: 0.75rem; opacity: 0.7;">
        Generated on ${new Date().toLocaleString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
      </p>
    </div>
  </div>
</body>
</html>
    `;
  };

  const handleViewDetails = (payment) => {
    setSelectedTransaction(payment);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingSpinner>
            <Spinner />
          </LoadingSpinner>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <Header>
          <Title>Payment History</Title>
          <Subtitle>Track all your payment transactions and receipts</Subtitle>
        </Header>

        <StatsGrid>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StatIcon bgColor="linear-gradient(135deg, #2d5016, #4a7c59)">
              <FaReceipt />
            </StatIcon>
            <StatValue>{stats.totalPayments}</StatValue>
            <StatLabel>Total Payments</StatLabel>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <StatIcon bgColor="linear-gradient(45deg, #00b894, #00a085)">
              <FaMoneyBillWave />
            </StatIcon>
            <StatValue>{formatCurrency(stats.totalAmount)}</StatValue>
            <StatLabel>Total Amount</StatLabel>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <StatIcon bgColor="linear-gradient(45deg, #00b894, #00a085)">
              <FaCheckCircle />
            </StatIcon>
            <StatValue>{stats.successfulPayments}</StatValue>
            <StatLabel>Successful</StatLabel>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <StatIcon bgColor="linear-gradient(45deg, #fdcb6e, #e17055)">
              <FaClock />
            </StatIcon>
            <StatValue>{stats.pendingPayments}</StatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
        </StatsGrid>

        <PaymentsList>
          <ListHeader>
            <ListTitle>Recent Payments</ListTitle>
            <FilterSelect
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Payments</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </FilterSelect>
          </ListHeader>

          {payments.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <FaReceipt />
              </EmptyIcon>
              <EmptyTitle>No payments found</EmptyTitle>
              <EmptyText>
                {filter === "all"
                  ? "You haven't made any payments yet."
                  : `No ${filter} payments found.`}
              </EmptyText>
            </EmptyState>
          ) : (
            payments.map((payment, index) => {
              const MethodIcon = getPaymentMethodIcon(payment.payment_method);
              return (
                <PaymentCard
                  key={payment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <PaymentHeader>
                    <PaymentInfo>
                      <PaymentId>Transaction #{payment.id}</PaymentId>
                      <PaymentDate>
                        {formatDate(payment.transaction_date || payment.created_at)}
                      </PaymentDate>
                    </PaymentInfo>
                    <PaymentAmount>
                      <Amount>{formatCurrency(payment.amount)}</Amount>
                      <StatusBadge status={payment.status || payment.payment_status}>
                        {payment.status || payment.payment_status}
                      </StatusBadge>
                    </PaymentAmount>
                  </PaymentHeader>

                  <PaymentDetails>
                    <DetailItem>
                      <DetailIcon
                        bgColor={getPaymentMethodColor(payment.payment_method)}
                      >
                        <MethodIcon />
                      </DetailIcon>
                      <DetailText>
                        {payment.payment_method === "cash"
                          ? "Cash"
                          : payment.payment_method === "cash_on_delivery"
                          ? "Cash on Delivery"
                          : payment.payment_method.toUpperCase()}
                      </DetailText>
                    </DetailItem>
                    {payment.subscription_id && (
                      <DetailItem>
                        <DetailText>
                          Meal Plan: {payment.meal_plan_title || `Subscription #${payment.subscription_id}`}
                        </DetailText>
                      </DetailItem>
                    )}
                    {payment.vendor_name && (
                      <DetailItem>
                        <DetailText>
                          Vendor: {payment.vendor_name}
                        </DetailText>
                      </DetailItem>
                    )}
                    {payment.external_transaction_id && (
                      <DetailItem>
                        <DetailText>
                          Transaction ID: {payment.external_transaction_id}
                        </DetailText>
                      </DetailItem>
                    )}
                    {payment.description && (
                      <DetailItem>
                        <DetailText>
                          {payment.description}
                        </DetailText>
                      </DetailItem>
                    )}
                  </PaymentDetails>

                  <PaymentActions>
                    <ActionButton
                      variant="secondary"
                      onClick={() => handleViewDetails(payment)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaEye />
                      View Details
                    </ActionButton>
                    <ActionButton
                      variant="primary"
                      onClick={() => handleDownloadReceipt(payment)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaDownload />
                      Receipt
                    </ActionButton>
                  </PaymentActions>
                </PaymentCard>
              );
            })
          )}
        </PaymentsList>
      </Content>

      {/* Transaction Details Modal */}
      {showModal && selectedTransaction && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Transaction Details</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <AmountHighlight>
                {formatCurrency(selectedTransaction.amount)}
              </AmountHighlight>

              <DetailSection>
                <SectionTitle>
                  <FaTag />
                  Transaction Information
                </SectionTitle>
                <DetailRow>
                  <DetailLabel>Transaction ID</DetailLabel>
                  <DetailValue>#{selectedTransaction.id}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Type</DetailLabel>
                  <DetailValue>
                    {selectedTransaction.transaction_type?.toUpperCase() || "Payment"}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    <StatusBadgeModal status={selectedTransaction.status || selectedTransaction.payment_status}>
                      {selectedTransaction.status || selectedTransaction.payment_status || "N/A"}
                    </StatusBadgeModal>
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>
                    <FaCalendar />
                    Transaction Date
                  </DetailLabel>
                  <DetailValue>
                    {formatFullDate(selectedTransaction.transaction_date || selectedTransaction.created_at)}
                  </DetailValue>
                </DetailRow>
                {selectedTransaction.completed_at && (
                  <DetailRow>
                    <DetailLabel>Completed At</DetailLabel>
                    <DetailValue>
                      {formatFullDate(selectedTransaction.completed_at)}
                    </DetailValue>
                  </DetailRow>
                )}
              </DetailSection>

              <DetailSection>
                <SectionTitle>
                  <FaCreditCard />
                  Payment Information
                </SectionTitle>
                <DetailRow>
                  <DetailLabel>Payment Method</DetailLabel>
                  <DetailValue>
                    {selectedTransaction.payment_method === "cash"
                      ? "Cash"
                      : selectedTransaction.payment_method === "mmg"
                      ? "MMG Mobile Money"
                      : selectedTransaction.payment_method === "card"
                      ? "Credit/Debit Card"
                      : selectedTransaction.payment_method?.toUpperCase() || "N/A"}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Amount</DetailLabel>
                  <DetailValue>{formatCurrency(selectedTransaction.amount)}</DetailValue>
                </DetailRow>
                {selectedTransaction.net_amount && selectedTransaction.net_amount !== selectedTransaction.amount && (
                  <DetailRow>
                    <DetailLabel>Net Amount</DetailLabel>
                    <DetailValue>{formatCurrency(selectedTransaction.net_amount)}</DetailValue>
                  </DetailRow>
                )}
                {selectedTransaction.fees && parseFloat(selectedTransaction.fees) > 0 && (
                  <DetailRow>
                    <DetailLabel>Fees</DetailLabel>
                    <DetailValue>{formatCurrency(selectedTransaction.fees)}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>Currency</DetailLabel>
                  <DetailValue>{selectedTransaction.currency || "GYD"}</DetailValue>
                </DetailRow>
              </DetailSection>

              {selectedTransaction.subscription_id && (
                <DetailSection>
                  <SectionTitle>
                    <FaStore />
                    Meal Plan Information
                  </SectionTitle>
                  {selectedTransaction.meal_plan_title && (
                    <DetailRow>
                      <DetailLabel>Meal Plan</DetailLabel>
                      <DetailValue>{selectedTransaction.meal_plan_title}</DetailValue>
                    </DetailRow>
                  )}
                  {selectedTransaction.vendor_name && (
                    <DetailRow>
                      <DetailLabel>Vendor</DetailLabel>
                      <DetailValue>{selectedTransaction.vendor_name}</DetailValue>
                    </DetailRow>
                  )}
                  <DetailRow>
                    <DetailLabel>Subscription ID</DetailLabel>
                    <DetailValue>#{selectedTransaction.subscription_id}</DetailValue>
                  </DetailRow>
                </DetailSection>
              )}

              {selectedTransaction.description && (
                <DetailSection>
                  <SectionTitle>
                    <FaFileInvoice />
                    Description
                  </SectionTitle>
                  <DetailRow>
                    <DetailValue style={{ textAlign: "left", maxWidth: "100%", fontWeight: "normal" }}>
                      {selectedTransaction.description}
                    </DetailValue>
                  </DetailRow>
                </DetailSection>
              )}

              {(selectedTransaction.external_transaction_id || 
                selectedTransaction.external_payment_id || 
                selectedTransaction.invoice_number || 
                selectedTransaction.receipt_number) && (
                <DetailSection>
                  <SectionTitle>
                    <FaFileInvoice />
                    Reference Numbers
                  </SectionTitle>
                  {selectedTransaction.external_transaction_id && (
                    <DetailRow>
                      <DetailLabel>External Transaction ID</DetailLabel>
                      <DetailValue>{selectedTransaction.external_transaction_id}</DetailValue>
                    </DetailRow>
                  )}
                  {selectedTransaction.external_payment_id && (
                    <DetailRow>
                      <DetailLabel>External Payment ID</DetailLabel>
                      <DetailValue>{selectedTransaction.external_payment_id}</DetailValue>
                    </DetailRow>
                  )}
                  {selectedTransaction.invoice_number && (
                    <DetailRow>
                      <DetailLabel>Invoice Number</DetailLabel>
                      <DetailValue>{selectedTransaction.invoice_number}</DetailValue>
                    </DetailRow>
                  )}
                  {selectedTransaction.receipt_number && (
                    <DetailRow>
                      <DetailLabel>Receipt Number</DetailLabel>
                      <DetailValue>{selectedTransaction.receipt_number}</DetailValue>
                    </DetailRow>
                  )}
                </DetailSection>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Payments;
